<template>
  <ModalView
    v-model="visible"
    :title="`${rule?.name || 'License Email'} Preview`"
    size="lg"
    scrollable
    hide-ok
    cancel-title="Close"
  >
    <LoadingMessage v-if="loading" message="Loading email preview" />
    <div v-else-if="previewError" class="alert alert-danger mb-0" role="alert">
      <div>{{ previewError.message || String(previewError) }}</div>
      <button type="button" class="btn btn-outline-danger btn-sm mt-2" @click="loadPreview">Try again</button>
    </div>
    <div v-else-if="preview" class="d-flex flex-column gap-3">
      <EmailPdfAttachments :attachments="preview.attachments" />
      <div>
        <div class="form-label">Subject</div>
        <div class="border rounded bg-light px-3 py-2">{{ preview.subject }}</div>
      </div>
      <div class="d-flex flex-row flex-wrap align-items-center justify-content-between gap-2">
        <div v-if="testRecipientEmail" class="small text-muted">
          Send a test copy to <strong>{{ testRecipientEmail }}</strong>.
        </div>
        <div v-else class="small text-danger">Your account does not have an email address.</div>
        <button
          type="button"
          class="btn btn-outline-primary"
          :disabled="sendingTest || !testRecipientEmail"
          @click="sendTestEmail"
        >
          <span v-if="sendingTest" class="spinner-border spinner-border-sm me-1" aria-hidden="true" />
          Send test email to me
        </button>
      </div>
      <div v-if="testError" class="alert alert-danger mb-0" role="alert">
        {{ testError.message || String(testError) }}
      </div>
      <iframe class="status-email-preview-frame" title="Styled license email preview" sandbox :srcdoc="preview.html" />
    </div>
  </ModalView>
</template>

<script>
import ModalView from '@/components/common/ModalView.vue';
import LoadingMessage from '@/components/common/LoadingMessage.vue';
import { License } from '@/models/License';
import EmailPdfAttachments from './EmailPdfAttachments.vue';

export default {
  components: { LoadingMessage, ModalView, EmailPdfAttachments },
  emits: ['update:modelValue'],
  props: {
    modelValue: Boolean,
    license: License,
    rule: Object,
    testRecipientEmail: String
  },
  data() {
    return {
      preview: null,
      loading: false,
      previewError: null,
      sendingTest: false,
      testError: null,
      loadToken: 0
    };
  },
  watch: {
    modelValue(value) {
      if (value) this.loadPreview();
    },
    rule() {
      if (this.modelValue) this.loadPreview();
    }
  },
  beforeUnmount() {
    this.loadToken += 1;
  },
  computed: {
    visible: {
      get() { return this.modelValue; },
      set(value) { this.$emit('update:modelValue', value); }
    }
  },
  methods: {
    async loadPreview() {
      if (!this.rule?.id) return;
      const token = ++this.loadToken;
      this.loading = true;
      this.previewError = null;
      this.testError = null;
      try {
        const preview = await this.license.previewStatusEmail(this.rule.id);
        if (token === this.loadToken) this.preview = preview;
      } catch (error) {
        if (token === this.loadToken) {
          this.preview = null;
          this.previewError = error;
        }
      } finally {
        if (token === this.loadToken) this.loading = false;
      }
    },
    async sendTestEmail() {
      if (!this.rule?.id || !this.testRecipientEmail || this.sendingTest) return;
      this.sendingTest = true;
      this.testError = null;
      try {
        const result = await this.license.sendStatusEmailTest(this.rule.id);
        this.$showToast(`Test ${this.rule.name} email sent to ${result?.recipientEmail || this.testRecipientEmail}`);
      } catch (error) {
        this.testError = error;
      } finally {
        this.sendingTest = false;
      }
    }
  }
};
</script>

<style scoped>
.status-email-preview-frame {
  width: 100%;
  min-height: 34rem;
  border: 1px solid var(--bs-border-color);
  border-radius: var(--bs-border-radius);
  background: #fff;
}
</style>
